import React, { useState, useMemo, useCallback, useEffect } from 'react'
import Swal from 'sweetalert2'
import { X, Plus } from 'lucide-react'
import { getSession } from '../../getSession'
import { sendMassAddTagNew, sendMassRemoveTagNew } from '../../utils/lead'

const AddRemoveTag = ({
  onClose = () => { },
  onSave = (payload) => { },
  tags = [],
  initial = {},
  mode = 'add',
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  registerSubmitHandler
}) => {
  const [selectedTags, setSelectedTags] = useState(() => {
    if (Array.isArray(initial.tagId)) return initial.tagId
    if (typeof initial.tagId === 'string' && initial.tagId) return initial.tagId.split(',')
    return []
  })
  const [tagQuery, setTagQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [schedule, setSchedule] = useState(initial.schedule || false)
  const [scheduleAt, setScheduleAt] = useState(initial.scheduleAt || '')
  const [error, setError] = useState('')

  const tagOptions = useMemo(() => {
    const list = (Array.isArray(tags) ? tags : [])
      .map((tag) => {
        if (typeof tag === 'string') {
          const t = tag.trim()
          return t ? { id: t, text: t } : null
        }
        const id = tag?.id ?? tag?.value ?? tag?.text ?? tag?.label
        const text = tag?.text ?? tag?.label ?? tag?.name ?? id
        if (!id || !text) return null
        return { id: String(id), text: String(text) }
      })
      .filter(Boolean)

    if (tagQuery.trim()) {
      return list.filter(t => t.text.toLowerCase().includes(tagQuery.toLowerCase()))
    }
    return list
  }, [tags, tagQuery])

  const addTag = (tagText) => {
    const t = String(tagText || '').trim()
    if (!t) return
    if (!selectedTags.includes(t)) {
      setSelectedTags(prev => [...prev, t])
    }
    setTagQuery('')
    setShowSuggestions(false)
  }

  const removeTag = (tagToRemove) => {
    setSelectedTags(prev => prev.filter(t => t !== tagToRemove))
  }

  const validate = (idsForPayload, shouldUseBatchId) => {
    setError('')
    if (!selectedTags || selectedTags.length === 0) { setError('Please add at least one tag'); return false }
    if (!idsForPayload && !shouldUseBatchId) { setError('No leads selected'); return false }
    if (schedule && !scheduleAt) { setError('Schedule date/time required when scheduling'); return false }
    return true
  }

  const handleSave = useCallback(async () => {
    const { userId, TokenId, token } = getSession()
    const numericUserId = Number(userId) || 0
    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!validate(idsForPayload, shouldUseBatchId)) return

    const payload = {
      Token: TokenId || token || '',
      Message: '',
      LoggedUserId: numericUserId,
      MAC_Address: '',
      IP_Address: '',
      Details: {
        IDS: idsForPayload,
        Userid: numericUserId,
        massbatchid: shouldUseBatchId ? numericBatchId : 0,
        Tags: selectedTags.join(','),
        StartDateTime: schedule ? String(scheduleAt || '') : ''
      },
      BroadcastName: ''
    }

    const actionLabel = mode === 'remove' ? 'remove' : 'add'

    try {
      const response = mode === 'remove'
        ? await sendMassRemoveTagNew(payload)
        : await sendMassAddTagNew(payload)

      const status = Number(response?.Status ?? response?.status ?? 0)
      const responseMessage = response?.Message || ''

      if (status === 1) {
        Swal.fire({ icon: 'success', title: 'Success', text: `Tag ${actionLabel}d successfully.` })
        onSave(payload)
        onClose()
        return
      }

      const errorMessage = responseMessage || `Failed to ${actionLabel} tag.`
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    } catch (err) {
      console.error('mass tag operation error', err)
      const errorMessage = err?.message || `An error occurred while trying to ${actionLabel} tag.`
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    }
  }, [
    selectedTags,
    schedule,
    scheduleAt,
    mode,
    selectedLeadIds,
    hashedIds,
    massOpBatchId,
    applyOnAllLeads,
    onClose,
    onSave
  ])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleSave)
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler, handleSave])

  const title = mode === 'remove' ? 'Remove Tag To Mass' : 'Add Tag To Mass'

  return (
    <div className="w-full max-w-lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>
          <div className="border border-gray-300 rounded p-2 focus-within:ring-1 focus-within:ring-blue-500 bg-white min-h-[42px]">
            <div className="flex flex-wrap gap-2 mb-1">
              {selectedTags.map((tag, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-blue-900">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="relative">
              <input
                type="text"
                className="w-full border-none outline-none text-sm p-1"
                placeholder={selectedTags.length > 0 ? "Add more tags..." : "Type tag and press Enter..."}
                value={tagQuery}
                onChange={(e) => {
                  setTagQuery(e.target.value)
                  setShowSuggestions(true)
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag(tagQuery)
                  }
                }}
              />
              {showSuggestions && tagOptions.length > 0 && (
                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-auto">
                  {tagOptions.map((opt) => (
                    <div
                      key={opt.id}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        addTag(opt.text)
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between items-center"
                    >
                      <span>{opt.text}</span>
                      {!selectedTags.includes(opt.text) && <Plus size={12} className="text-gray-400" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {schedule && (<div>
          <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>)}

        <div className="flex items-center justify-between">
          <label className="inline-flex items-center">
            <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className=" rounded-[3px] mr-2" />
            On Schedule Date And Time
          </label>

        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
      </div>
    </div>
  )
}

export default AddRemoveTag
